import React, { useEffect, useState, useCallback } from "react";
import { Bell } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { formatRelativeTime } from "@/lib/format";

const TYPE_DOT = {
  info: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-destructive",
};

export default function NotificationBell({ items, onOpen }) {
  const [notifications, setNotifications] = useState(items || []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (items) setNotifications(items);
  }, [items]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleOpenChange = async (open) => {
    if (open && unreadCount > 0) {
      try {
        await api.post("/notifications/read-all");
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (e) {
        console.error("Failed to mark notifications as read:", e);
      }
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className="relative h-9 w-9 rounded-md flex items-center justify-center hover:bg-accent transition-colors" data-testid="notification-bell-button">
          <Bell className="h-4.5 w-4.5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-destructive text-white text-[10px] flex items-center justify-center" data-testid="notification-unread-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 && (
            <p className="text-sm text-muted-foreground px-2 py-6 text-center">No notifications yet.</p>
          )}
          {notifications.slice(0, 15).map((n) => (
            <div key={n.id} className="flex gap-2.5 px-2 py-2.5 rounded-md hover:bg-accent transition-colors" data-testid={`notification-item-${n.id}`}>
              <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${TYPE_DOT[n.type] || TYPE_DOT.info}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium leading-snug">{n.title}</p>
                <p className="text-xs text-muted-foreground leading-snug mt-0.5">{n.message}</p>
                <p className="text-[11px] text-muted-foreground/70 mt-1">{formatRelativeTime(n.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
